import { useVideoSwipeOptional } from "@/contexts/VideoSwipeContext";
import { useNetworkType } from "@/hooks/useNetworkType";
import {
    bunnyMp4Url,
    getBunnyPlaybackUrlForNetwork,
    getBunnyUrls,
    HEADER_VIDEO_BUNNY_ID,
} from "@/lib/bunny-urls";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

const BODY_BG_VIDEO_CLASS = "has-background-video";

/**
 * Background Video: Spielt das Hero-Video dauerhaft im Hintergrund ab
 * Auf Marketing-Routen (/, /booking, /session-invite, …), NICHT im Dashboard
 */
const BackgroundVideo: React.FC = () => {
  const location = useLocation();
  const videoSwipe = useVideoSwipeOptional();
  const networkType = useNetworkType();
  const videoRef = videoSwipe?.videoRef;
  const [isVisible, setIsVisible] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const [useAdaptiveQuality, setUseAdaptiveQuality] = useState(false);
  const internalVideoRef = useRef<HTMLVideoElement>(null);
  const actualVideoRef = videoRef || internalVideoRef;

  // Zielquelle: adaptive HLS-URL, sobald der kontrollierte Start erfolgt ist
  const adaptiveVideoUrl = getBunnyPlaybackUrlForNetwork(
    HEADER_VIDEO_BUNNY_ID,
    networkType
  );
  const bunnyUrls = getBunnyUrls(HEADER_VIDEO_BUNNY_ID);
  const posterUrl = bunnyUrls.thumbnail;
  const bootstrapVideoUrl =
    networkType === "wifi"
      ? bunnyMp4Url(bunnyUrls.pullZone, HEADER_VIDEO_BUNNY_ID, 720)
      : bunnyMp4Url(bunnyUrls.pullZone, HEADER_VIDEO_BUNNY_ID, 480);
  const activeVideoUrl = useAdaptiveQuality ? adaptiveVideoUrl : bootstrapVideoUrl;
  const isHls = activeVideoUrl.endsWith(".m3u8");

  // Prüfe Route: Header-Video nur auf Marketing-Seiten, NICHT im Dashboard/Admin
  const isMarketingPage =
    location.pathname === "/" ||
    location.pathname === "/booking" ||
    location.pathname === "/session-invite";

  // Workaround: Video erst anzeigen, wenn Dashboard vollständig geladen ist
  // Verhindert, dass das Video während des Ladens durchscheint
  // ABER: Nur auf Marketing-Seiten, nicht im Dashboard
  const shouldShowVideo =
    isMarketingPage && (videoSwipe?.isDashboardLoaded ?? true);

  // Lade das Video bewusst verzögert, damit der First Paint nicht mit HLS konkurriert.
  useEffect(() => {
    if (!shouldShowVideo) {
      setShouldLoadVideo(false);
      setVideoLoaded(false);
      setIsVisible(false);
      setUseAdaptiveQuality(false);
      return;
    }

    let timerId: number | null = null;
    const armLoading = () => {
      if (timerId) {
        window.clearTimeout(timerId);
      }
      timerId = window.setTimeout(() => {
        setUseAdaptiveQuality(false);
        setShouldLoadVideo(true);
      }, 900);
    };

    if (document.visibilityState === "visible") {
      armLoading();
    } else {
      const onVisible = () => {
        if (document.visibilityState === "visible") {
          armLoading();
          document.removeEventListener("visibilitychange", onVisible);
        }
      };
      document.addEventListener("visibilitychange", onVisible);
      return () => {
        if (timerId) {
          window.clearTimeout(timerId);
        }
        document.removeEventListener("visibilitychange", onVisible);
      };
    }

    return () => {
      if (timerId) {
        window.clearTimeout(timerId);
      }
    };
  }, [shouldShowVideo]);

  // Kontrollierter Qualitätsstart:
  // 1) Erst leichtes MP4 starten  2) Danach auf adaptive HLS-Qualität umschalten.
  useEffect(() => {
    if (!shouldShowVideo || !shouldLoadVideo || !videoLoaded || !isVisible) {
      return;
    }
    // Landing/Booking/Session-Invite: nur Bootstrap-MP4, keine HLS-Segment-Requests.
    if (
      location.pathname === "/" ||
      location.pathname === "/booking" ||
      location.pathname === "/session-invite"
    ) {
      return;
    }
    if (useAdaptiveQuality || bootstrapVideoUrl === adaptiveVideoUrl) {
      return;
    }

    const switchTimerId = window.setTimeout(() => {
      setUseAdaptiveQuality(true);
    }, 1800);

    return () => {
      window.clearTimeout(switchTimerId);
    };
  }, [
    adaptiveVideoUrl,
    bootstrapVideoUrl,
    isVisible,
    location.pathname,
    shouldLoadVideo,
    shouldShowVideo,
    useAdaptiveQuality,
    videoLoaded,
  ]);

  // Wenn das BackgroundVideo aktiv ist, müssen Body/#root/.app-shell transparent sein,
  // sonst sieht man nur den globalen "bg-background" Layer statt dem Video.
  useEffect(() => {
    if (!shouldShowVideo) return;
    document.body.classList.add(BODY_BG_VIDEO_CLASS);
    return () => {
      document.body.classList.remove(BODY_BG_VIDEO_CLASS);
    };
  }, [shouldShowVideo]);

  // Video-Lade-Handler
  useEffect(() => {
    const video = actualVideoRef.current;
    if (!video || !shouldLoadVideo) return;

    const handleLoadedMetadata = () => {
      if (import.meta.env.DEV) {
        console.log("🎬 Background Video: Metadaten geladen", {
          duration: video.duration,
          videoWidth: video.videoWidth,
          videoHeight: video.videoHeight,
        });
      }
      setVideoLoaded(true);
      setVideoError(null);
    };

    const handleLoadedData = () => {
      // Log entfernt - nicht notwendig
    };

    const handleCanPlay = () => {
      if (import.meta.env.DEV) {
        console.log("🎬 Background Video: Kann abgespielt werden");
      }
      // Video sichtbar machen, auch wenn Autoplay blockiert wird
      setIsVisible(true);
      if (shouldShowVideo) {
        video.play().catch(e => {
          if (import.meta.env.DEV) {
            console.warn("🎬 Background Video autoplay failed:", e);
          }
          setVideoError(`Autoplay fehlgeschlagen: ${e.message}`);
          // Trotzdem sichtbar machen, auch wenn Autoplay blockiert wird
          setIsVisible(true);
        });
      }
    };

    const handleCanPlayThrough = () => {
      if (import.meta.env.DEV) {
        console.log("🎬 Background Video: Kann vollständig abgespielt werden");
      }
      setIsVisible(true);
      if (shouldShowVideo && video.paused) {
        video.play().catch(e => {
          if (import.meta.env.DEV) {
            console.warn("🎬 Background Video canplaythrough play failed:", e);
          }
        });
      }
    };

    const handleError = (e: Event) => {
      const error = video.error;
      let errorMsg = "Unbekannter Fehler";
      if (error) {
        switch (error.code) {
          case error.MEDIA_ERR_ABORTED:
            errorMsg = "Video-Laden abgebrochen";
            break;
          case error.MEDIA_ERR_NETWORK:
            errorMsg = "Netzwerkfehler beim Laden des Videos";
            break;
          case error.MEDIA_ERR_DECODE:
            errorMsg = "Video-Dekodierung fehlgeschlagen";
            break;
          case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
            errorMsg = "Video-Format nicht unterstützt oder Pfad ungültig";
            break;
        }
      }
      console.error("🎬 Background Video Fehler:", errorMsg, video.src);
      setVideoError(errorMsg);
    };

    const handlePlay = () => {
      // Log entfernt - nicht notwendig
      setIsVisible(true);
    };

    const handlePlaying = () => {
      // Log entfernt - nicht notwendig
      setIsVisible(true);
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("loadeddata", handleLoadedData);
    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("canplaythrough", handleCanPlayThrough);
    video.addEventListener("error", handleError);
    video.addEventListener("play", handlePlay);
    video.addEventListener("playing", handlePlaying);

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("loadeddata", handleLoadedData);
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("canplaythrough", handleCanPlayThrough);
      video.removeEventListener("error", handleError);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("playing", handlePlaying);
    };
  }, [actualVideoRef, shouldShowVideo, shouldLoadVideo]);

  // Stelle sicher, dass das Video startet, wenn es angezeigt werden soll
  useEffect(() => {
    const video = actualVideoRef.current;
    if (video && shouldShowVideo && shouldLoadVideo && videoLoaded) {
      // Prüfe ob Video bereits läuft
      if (video.paused) {
        video.play().catch(e => {
          if (import.meta.env.DEV) {
            console.warn(
              "🎬 Background Video: Play-Versuch fehlgeschlagen:",
              e
            );
          }
          setVideoError(`Play fehlgeschlagen: ${e.message}`);
          // Video trotzdem sichtbar machen, auch wenn Autoplay blockiert wird
          setIsVisible(true);
        });
      } else {
        // Video läuft bereits, sichtbar machen
        setIsVisible(true);
      }
    }
  }, [actualVideoRef, shouldShowVideo, shouldLoadVideo, videoLoaded]);

  // Prüfe ob alles ausgeblendet ist (swipeProgress >= 1)
  const isFullySwiped =
    videoSwipe?.swipeProgress && videoSwipe.swipeProgress >= 1;

  // Debug-Logging (nur bei tatsächlichen Änderungen)
  const prevVideoStatusRef = useRef<string | null>(null);
  useEffect(() => {
    if (import.meta.env.DEV) {
      const currentStatus = JSON.stringify({
        shouldShowVideo,
        videoLoaded,
        videoError,
        isVisible,
        hasVideoRef: !!actualVideoRef.current,
        videoSrc: actualVideoRef.current?.src,
      });
      if (prevVideoStatusRef.current !== currentStatus) {
        // Debug-Log deaktiviert
        // console.log("🎬 Background Video Status:", {
        //   shouldShowVideo,
        //   videoLoaded,
        //   videoError,
        //   isVisible,
        //   hasVideoRef: !!actualVideoRef.current,
        //   videoSrc: actualVideoRef.current?.src,
        // });
        prevVideoStatusRef.current = currentStatus;
      }
    }
  }, [shouldShowVideo, videoLoaded, videoError, isVisible, actualVideoRef]);

  // Nicht rendern, wenn Dashboard noch nicht geladen ist
  if (!shouldShowVideo) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 pointer-events-none overflow-hidden"
      style={{
        // Video ist immer im Hintergrund, aber komplett hinter dem Dashboard
        // z-index muss niedriger sein als Logo (20), Swipe-Text (35) und Content (40)
        // aber höher als -999, damit es nicht von anderen Elementen verdeckt wird
        // WICHTIG: Auf Marketing-Seiten (ohne Swipe-Kontext) darf das Video nicht mit zIndex=-1
        // "hinter" den gesamten Seiten-Stack rutschen (sonst sieht man nur den blauen Hintergrund).
        zIndex: videoSwipe ? (isFullySwiped ? 0 : -1) : 0,
        // Fade-in Animation über 0.5 Sekunden
        opacity: isVisible ? 1 : 0,
        transition: "opacity 0.5s ease-in",
      }}
    >
      <video
        ref={actualVideoRef}
        className="absolute inset-0 w-full h-full object-cover"
        autoPlay
        muted
        loop
        playsInline
        preload="none"
        poster={posterUrl}
        // WICHTIG: kein position: fixed mehr - Video ist absolut innerhalb des Wrappers
      >
        <source
          src={shouldLoadVideo ? activeVideoUrl : ""}
          type={isHls ? "application/x-mpegURL" : "video/mp4"}
        />
        Your browser does not support the video tag.
      </video>
      {/* Optional: Dunkler Overlay für bessere Lesbarkeit - reduziert für bessere Sichtbarkeit */}
      <div className="absolute inset-0 bg-black/10 pointer-events-none" />
      {/* Debug-Anzeige im DEV-Modus */}
      {import.meta.env.DEV && videoError && (
        <div className="absolute top-4 left-4 bg-red-500/80 text-white p-2 rounded text-xs z-50 pointer-events-auto">
          ⚠️ Video-Fehler: {videoError}
        </div>
      )}
    </div>
  );
};

export default BackgroundVideo;
